import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import { Drawer } from 'rsuite';
import { toast } from 'react-toastify';
import * as FileSaver from 'file-saver';

import { Loading, PageHeader, Pagination } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import RowPaginator from '../../components/RowPaginator/RowPaginator';
import PermissionGate from '../../components/PermissionGate/PermissionGate';

import useComponentState from '../../utils/useComponentState';
import { usePermissions } from '../../hooks/usePermissions';
import { getPopularMerchants, downloadPopularMerchants } from '../../api/earning';
import { EARNINGS_PERMISSIONS } from '../../constants/permissions';

import styles from './PopularMerchants.module.scss';

const PopularMerchants = (props) => {
  const { can } = usePermissions();
  const {
    auth: { token },
  } = props.auth;

  const [state, setState] = useComponentState({
    tableHeading: {
      column: [
        'Merchant ID',
        'Merchant Name',
        'Redemption Count',
        'Total Redeemed Amount (LKR)',
      ],
    },
    merchantsData: [],
    loadingError: '',
    loading: false,
    currentPage: 0,
    totalPages: 0,
    rowSize: 25,
    showDrawer: false,
    drawerData: null,
    downloadStatus: '',
  });

  const {
    tableHeading,
    merchantsData,
    loadingError,
    loading,
    currentPage,
    totalPages,
    rowSize,
  } = state;

  useEffect(() => {
    fetchData(0);
  }, []);

  const fetchData = async (page = 0, pageSize = state.rowSize) => {
    setState({ loading: true, merchantsData: [], loadingError: '' });
    try {
      const response = await getPopularMerchants(token, page, pageSize);
      setState({
        merchantsData: response.list || [],
        totalPages: response.totalPageCount || 0,
        currentPage: page,
      });
    } catch (err) {
      setState({
        loadingError: 'Oops, something went wrong. Please try again.',
      });
    }
    setState({ loading: false });
  };

  const handlePagination = (page) => {
    setState({ currentPage: page });
    fetchData(page);
  };

  const handleRowPaginationChange = (event) => {
    const newRowSize = Number(event.target.value);
    setState({ rowSize: newRowSize, currentPage: 0 });
    fetchData(0, newRowSize);
  };

  const handleDownload = async () => {
    setState({ downloadStatus: 'DOWNLOADING' });
    try {
      const blob = await downloadPopularMerchants(token);
      FileSaver.saveAs(blob, 'Popular_Merchants.csv');
      setState({ downloadStatus: '' });
    } catch (err) {
      setState({ downloadStatus: '' });
      toast.error(err?.message || 'Failed to download. Please try again.');
    }
  };

  const handleDrawerOpen = (rowData) => {
    if (!rowData) return;
    setState({ showDrawer: true, drawerData: rowData });
  };

  const handleDrawerClose = () => {
    setState({ showDrawer: false, drawerData: null });
  };

  const formatAmount = (value) =>
    value !== undefined && value !== null
      ? Number(value)
          .toFixed(2)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      : 'N/A';

  const renderDrawer = () => {
    const data = state.drawerData;
    if (!data) return null;

    return (
      <>
        <Drawer.Header>
          <div className={styles.drawer_header}>
            <Drawer.Title>Popular Merchant Details</Drawer.Title>
          </div>
        </Drawer.Header>

        <Drawer.Body>
          <div className={styles.drawer_body}>
            <div className={styles.detail_row}>
              <strong>Merchant ID:</strong>
              <span>{data.merchantId ?? 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Merchant Name:</strong>
              <span>{data.merchantName || 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Redemption Count:</strong>
              <span>{data.redemptionCount ?? 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Total Redeemed Amount (LKR):</strong>
              <span>{formatAmount(data.totalRedeemedAmount)}</span>
            </div>
          </div>
        </Drawer.Body>
      </>
    );
  };

  if (!can(EARNINGS_PERMISSIONS.POPULAR_MERCHANTS.VIEW)) {
    return (
      <div className={styles.root_div}>
        <PageHeader text='Popular Merchants' />
        <div className={styles.access_denied}>
          <p className={styles.access_denied_title}>Access Denied</p>
          <p className={styles.access_denied_message}>
            You do not have permission to access this feature. Please contact
            your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root_div}>
      <div>
        <PageHeader text='Popular Merchants' />
        <div className={styles.search_area_block}>
          <div className={styles.user_input}>
            <RowPaginator
              handleRowPaginationChange={handleRowPaginationChange}
              rowSize={rowSize}
            />
            <PermissionGate
              permission={EARNINGS_PERMISSIONS.POPULAR_MERCHANTS.DOWNLOAD}
            >
              <div className={styles.download_button_block}>
                <Button
                  variant='secondary'
                  onClick={handleDownload}
                  disabled={state.downloadStatus === 'DOWNLOADING'}
                >
                  {state.downloadStatus === 'DOWNLOADING'
                    ? 'Downloading...'
                    : 'Download All'}
                </Button>
              </div>
            </PermissionGate>
          </div>
        </div>
        {loading ? (
          <Loading text='Loading' />
        ) : (
          <>
            <DataTable
              tableData={merchantsData}
              tableheading={tableHeading}
              handleOnClick={handleDrawerOpen}
              tag='popular-merchants'
              fetchError={loadingError}
              notFoundMessage={loadingError}
              search_filter_loading={false}
            />
            {!loadingError && totalPages > 0 && (
              <div className={styles.dualPaginateContainer}>
                <div className={styles.pages}>
                  <span>
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  &nbsp;&nbsp;&nbsp;
                  <Pagination
                    pageCount={totalPages}
                    onChange={handlePagination}
                    currentPage={currentPage}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Drawer
        show={state.showDrawer}
        onHide={handleDrawerClose}
        placement='right'
      >
        {renderDrawer()}
      </Drawer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(PopularMerchants);
